import { Type, type Static } from "@sinclair/typebox";
import { PaymentMethod, RideCategory, TripKind } from "./domain.ts";

/** Tipos de corrida (docs/specs/trip-types.md): staff-defined trip types and the groups they sit in. */

/** Preset drawings every app ships; an unknown key falls back to `car` or `moto` by `vehicleKind`. */
export const TRIP_TYPE_ICONS = [
  "car", "car_comfort", "car_premium", "suv", "van", "moto", "package", "package_moto", "pet", "women", "eco", "accessible", "star",
] as const;
export type TripTypeIcon = (typeof TRIP_TYPE_ICONS)[number];

export const VehicleKind = Type.Union([Type.Literal("car"), Type.Literal("motorcycle")], { $id: "VehicleKind" });
export type VehicleKind = Static<typeof VehicleKind>;

export const Gender = Type.Union([Type.Literal("female"), Type.Literal("male")], { $id: "Gender" });
export type Gender = Static<typeof Gender>;
/** Who may request (rider) or drive (driver) a type. */
export const GenderRule = Type.Union([Type.Literal("any"), Type.Literal("female"), Type.Literal("male")], { $id: "GenderRule" });
export type GenderRule = Static<typeof GenderRule>;

/** `always`: on whenever eligible · `opt_out`: on until the driver turns it off · `opt_in`: off until the driver turns it on. */
export const DriverChoice = Type.Union([Type.Literal("always"), Type.Literal("opt_out"), Type.Literal("opt_in")], { $id: "DriverChoice" });
export type DriverChoice = Static<typeof DriverChoice>;

export const TripTypeGroupView = Type.Object(
  { id: Type.String(), name: Type.String(), description: Type.Union([Type.String(), Type.Null()]) },
  { $id: "TripTypeGroupView" },
);
export type TripTypeGroupView = Static<typeof TripTypeGroupView>;

/** What the apps show about a type: on quotes, the catalog, the driver's Conta list. */
export const TripTypeView = Type.Object(
  {
    id: RideCategory,
    service: TripKind,
    name: Type.String(),
    /** "" when staff left it blank. */
    description: Type.String(),
    details: Type.Union([Type.String(), Type.Null()]),
    badge: Type.Union([Type.String(), Type.Null()]),
    /** A `TRIP_TYPE_ICONS` key (apps fall back by `vehicleKind` on an unknown one). */
    icon: Type.String(),
    /** Absolute URL of the uploaded icon; null = draw `icon`. */
    iconUrl: Type.Union([Type.String(), Type.Null()]),
    groupId: Type.Union([Type.String(), Type.Null()]),
    vehicleKind: VehicleKind,
    /** Passengers. 0 on delivery types. */
    seats: Type.Integer(),
    /** Delivery types only. */
    maxWeightKg: Type.Union([Type.Number(), Type.Null()]),
    packageHint: Type.Union([Type.String(), Type.Null()]),
    allowsPets: Type.Boolean(),
    allowsScheduling: Type.Boolean(),
    maxStops: Type.Integer(),
    paymentMethods: Type.Array(PaymentMethod),
    /** Cupons apply to it (otherwise the app hides the coupon field for it). */
    allowsPromos: Type.Boolean(),
    /** Rider cancellation fee (to the driver) once the free window is over. */
    cancellationFeeCents: Type.Integer({ minimum: 0 }),
  },
  { $id: "TripTypeView" },
);
export type TripTypeView = Static<typeof TripTypeView>;

/** `GET /trip-types`: active types (riders get only those they may request), in display order. */
export const TripTypeCatalog = Type.Object(
  { groups: Type.Array(TripTypeGroupView), types: Type.Array(TripTypeView) },
  { $id: "TripTypeCatalog" },
);
export type TripTypeCatalog = Static<typeof TripTypeCatalog>;

/** What the website shows about a type: how it looks and how much it carries, none of its fees or rules. */
export const PublicTripType = Type.Pick(
  TripTypeView,
  ["id", "service", "name", "description", "badge", "icon", "iconUrl", "vehicleKind", "seats", "maxWeightKg", "packageHint"],
  { $id: "PublicTripType" },
);
export type PublicTripType = Static<typeof PublicTripType>;

/** `GET /public/trip-types`: the same types as the catalog, in display order, without a session. */
export const PublicTripTypeCatalog = Type.Object({ types: Type.Array(PublicTripType) }, { $id: "PublicTripTypeCatalog" });
export type PublicTripTypeCatalog = Static<typeof PublicTripTypeCatalog>;

/** Why a quote option can't take this request. */
export const UnavailableReason = Type.Union(
  [Type.Literal("pet_not_allowed"), Type.Literal("too_many_stops"), Type.Literal("schedule_not_allowed"), Type.Literal("card_not_accepted"), Type.Literal("pix_not_accepted"), Type.Literal("cash_not_accepted")],
  { $id: "UnavailableReason" },
);
export type UnavailableReason = Static<typeof UnavailableReason>;
export const UNAVAILABLE_REASON_LABELS: Record<UnavailableReason, string> = {
  pet_not_allowed: "Não aceita pet",
  too_many_stops: "Paradas demais para esta opção",
  schedule_not_allowed: "Não pode ser agendada",
  card_not_accepted: "Não aceita cartão",
  pix_not_accepted: "Não aceita Pix",
  cash_not_accepted: "Não aceita pagamento na hora",
};

/** One row of the driver's Conta → Tipos de corrida. */
export const DriverTripTypeView = Type.Object(
  {
    type: TripTypeView,
    eligible: Type.Boolean(),
    /** pt-BR, when not eligible ("Carro com mais de 6 anos"). */
    reason: Type.Union([Type.String(), Type.Null()]),
    /** Offered while online (eligible and switched on). */
    enabled: Type.Boolean(),
    /** The driver may switch it (eligible and not `always`). */
    canToggle: Type.Boolean(),
  },
  { $id: "DriverTripTypeView" },
);
export type DriverTripTypeView = Static<typeof DriverTripTypeView>;

// ── Admin ────────────────────────────────────────────────────────────────────────────────

const slug = (max: number) => Type.String({ pattern: `^[a-z0-9][a-z0-9-]{1,${max - 1}}$` });

/** Every editable field of a type (admin create/update). */
export const TripTypeFields = Type.Object({
  service: TripKind,
  name: Type.String({ minLength: 2, maxLength: 40 }),
  description: Type.String({ maxLength: 120 }),
  details: Type.Union([Type.String({ maxLength: 600 }), Type.Null()]),
  badge: Type.Union([Type.String({ maxLength: 16 }), Type.Null()]),
  /** A `TRIP_TYPE_ICONS` key. */
  icon: Type.String({ minLength: 1, maxLength: 32 }),
  groupId: Type.Union([Type.String(), Type.Null()]),
  active: Type.Boolean(),
  seats: Type.Integer({ minimum: 0, maximum: 60 }),
  maxWeightKg: Type.Union([Type.Number({ minimum: 0, maximum: 2000 }), Type.Null()]),
  packageHint: Type.Union([Type.String({ maxLength: 60 }), Type.Null()]),
  riderGender: GenderRule,
  allowPets: Type.Boolean(),
  petFeeCents: Type.Union([Type.Integer({ minimum: 0, maximum: 100_000 }), Type.Null()]),
  maxStops: Type.Union([Type.Integer({ minimum: 0, maximum: 10 }), Type.Null()]),
  allowScheduling: Type.Boolean(),
  paymentPix: Type.Boolean(),
  paymentCard: Type.Boolean(),
  /** Pagar na hora; absent = on. */
  paymentCash: Type.Optional(Type.Boolean()),
  allowPromos: Type.Boolean(),
  cancellationFeeCents: Type.Integer({ minimum: 0, maximum: 100_000 }),
  vehicleKind: VehicleKind,
  maxVehicleAgeYears: Type.Union([Type.Integer({ minimum: 0, maximum: 60 }), Type.Null()]),
  vehicleModels: Type.Array(Type.String({ minLength: 2, maxLength: 60 }), { maxItems: 200 }),
  minDriverRating: Type.Union([Type.Number({ minimum: 1, maximum: 5 }), Type.Null()]),
  minDriverTrips: Type.Union([Type.Integer({ minimum: 0, maximum: 100_000 }), Type.Null()]),
  driverGender: GenderRule,
  requiresApproval: Type.Boolean(),
  driverChoice: DriverChoice,
});
export type TripTypeFields = Static<typeof TripTypeFields>;

export const FareTableInput = Type.Object({
  baseCents: Type.Integer({ minimum: 0, maximum: 100_000 }),
  perKmCents: Type.Integer({ minimum: 0, maximum: 100_000 }),
  perMinuteCents: Type.Integer({ minimum: 0, maximum: 100_000 }),
  minimumCents: Type.Integer({ minimum: 0, maximum: 100_000 }),
  commissionBps: Type.Integer({ minimum: 0, maximum: 5000 }),
});
export type FareTableInput = Static<typeof FareTableInput>;

export const TripTypeCreate = Type.Composite([
  Type.Object({
    id: slug(32),
    /** First fare table, applied to `cityIds` (every city when empty). */
    fare: FareTableInput,
    cityIds: Type.Array(Type.String(), { maxItems: 200 }),
  }),
  TripTypeFields,
]);
export type TripTypeCreate = Static<typeof TripTypeCreate>;

export const TripTypeGroupInput = Type.Object({
  name: Type.String({ minLength: 2, maxLength: 40 }),
  description: Type.Union([Type.String({ maxLength: 120 }), Type.Null()]),
  active: Type.Boolean(),
});
export const TripTypeGroupCreate = Type.Composite([Type.Object({ id: slug(32) }), TripTypeGroupInput]);

/** `PUT /admin/trip-types/order`: every group in order with its types in order; `id: null` = no group. */
export const TripTypeOrderInput = Type.Object({
  groups: Type.Array(Type.Object({ id: Type.Union([Type.String(), Type.Null()]), typeIds: Type.Array(RideCategory) })),
});

/** Admin list row: the type with its rules, and how it stands in each city. */
export const AdminTripType = Type.Composite([
  Type.Object({
    id: RideCategory,
    sortOrder: Type.Integer(),
    iconUrl: Type.Union([Type.String(), Type.Null()]),
    /** Cities (active ones) without a fare table for the type. */
    citiesWithoutFare: Type.Array(Type.String()),
    tripsLast30Days: Type.Integer(),
    createdAt: Type.String(),
    updatedAt: Type.String(),
  }),
  TripTypeFields,
]);
export type AdminTripType = Static<typeof AdminTripType>;

export const AdminTripTypeGroup = Type.Object({
  id: Type.String(), name: Type.String(), description: Type.Union([Type.String(), Type.Null()]), sortOrder: Type.Integer(), active: Type.Boolean(),
});
export type AdminTripTypeGroup = Static<typeof AdminTripTypeGroup>;

export const AdminTripTypeList = Type.Object({ groups: Type.Array(AdminTripTypeGroup), types: Type.Array(AdminTripType) });
export type AdminTripTypeList = Static<typeof AdminTripTypeList>;

export const TRIP_TYPE_DEFAULT_FARE: Static<typeof FareTableInput> = { baseCents: 400, perKmCents: 130, perMinuteCents: 38, minimumCents: 900, commissionBps: 2000 };
